import { useEffect, useMemo, useState } from "react";
import type { MbRunSummary } from "#shared/schemas/mbEnrich.ts";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import {
  useMbStatus,
  usePauseMbPass,
  useResumeMbPass,
  useStartMbBatch,
  useStopMbPass,
} from "@web/features/settings/mbApi.ts";
import { formatTrackPulse } from "@web/features/settings/enrichProgress.ts";
import { cn } from "@web/lib/utils.ts";

function formatElapsed(ms: number): string {
  const sec = Math.max(0, Math.round(ms / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
}

function SummaryBlock({ summary }: { summary: MbRunSummary }) {
  return (
    <section className="space-y-3 rounded-md border border-line bg-panel px-3 py-3">
      <div>
        <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">
          Run complete
        </h3>
        <p className="mt-1 text-sm text-muted">
          {summary.tracks_processed.toLocaleString()} tracks ·{" "}
          {summary.requests_made.toLocaleString()} requests ·{" "}
          {formatElapsed(summary.elapsed_ms)}
        </p>
      </div>

      <div className="space-y-1">
        {summary.by_type.map((row) => (
          <div key={row.entity_type} className="flex justify-between gap-3 text-sm">
            <span className="text-muted capitalize">{row.entity_type.replace("-", " ")}</span>
            <span className="tabular-nums text-text">
              {row.new_count.toLocaleString()}
              {row.partial_count > 0
                ? ` (${row.partial_count} partial · ${row.new_count - row.partial_count} ok)`
                : " ok"}
            </span>
          </div>
        ))}
        {summary.by_type.length === 0 && (
          <p className="text-sm text-muted">No new entities stored.</p>
        )}
      </div>
    </section>
  );
}

/** Playlist workbench inspect — enrich this playlist end-to-end. */
export function PlaylistEnrichSection({
  playlistId,
  localPlaylistId,
}: {
  playlistId?: string | null;
  localPlaylistId?: string | null;
}) {
  const statusQ = useMbStatus(
    {
      playlistId: playlistId ?? null,
      localPlaylistId: localPlaylistId ?? null,
    },
    {
    refetchInterval: (q) => {
      const run = q.state.data?.run;
      return run?.status === "running" || run?.status === "paused" ? 1_500 : 4_000;
    },
    },
  );
  const startBatch = useStartMbBatch();
  const pausePass = usePauseMbPass();
  const resumePass = useResumeMbPass();
  const stopPass = useStopMbPass();
  const [tick, setTick] = useState(0);
  const scopedPlaylistId = playlistId ?? null;
  const scopedLocalPlaylistId = localPlaylistId ?? null;

  const status = statusQ.data;
  const run = status?.run ?? null;
  const thisPlaylistRun =
    (scopedPlaylistId !== null && run?.playlist_id === scopedPlaylistId) ||
    (scopedLocalPlaylistId !== null && run?.local_playlist_id === scopedLocalPlaylistId);
  const active =
    thisPlaylistRun && (run?.status === "running" || run?.status === "paused");
  const otherBusy =
    Boolean(run) &&
    (run?.status === "running" || run?.status === "paused") &&
    !thisPlaylistRun;

  useEffect(() => {
    if (!active || run?.status !== "running") return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [active, run?.status]);

  const live = useMemo(() => {
    void tick;
    if (!run || !thisPlaylistRun) return null;
    const started = Date.parse(run.started_at);
    const elapsedMs = Number.isNaN(started) ? 0 : Math.max(0, Date.now() - started);
    const processed = Math.max(0, run.tracks_done);
    const remaining = Math.max(0, run.tracks_total - processed);
    const estimate = status?.estimates.all ?? null;
    const etaMs =
      processed > 0 && elapsedMs > 0
        ? Math.round((elapsedMs / processed) * remaining)
        : estimate
          ? Math.round((((estimate.time_sec_min + estimate.time_sec_max) / 2) * 1000))
          : 0;
    return {
      elapsedLabel: formatElapsed(elapsedMs),
      etaLabel: remaining > 0 ? formatElapsed(etaMs) : "0s",
      pct:
        run.tracks_total > 0
          ? Math.min(100, (run.tracks_done / run.tracks_total) * 100)
          : 0,
    };
  }, [run, thisPlaylistRun, tick]);

  if (statusQ.isLoading) return <Skeleton className="h-32 w-full" />;

  const estimate = status?.estimates.all;
  const contactMissing = status?.settings.contact_source === "missing";
  const scopeLabel = scopedLocalPlaylistId ? "local playlist" : "playlist";

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <p className="text-sm text-muted">
          Fetch missing MusicBrainz and AcousticBrainz data for tracks on this {scopeLabel}.
          Already stored metadata is kept; reruns only fill tracks we do not have yet.
        </p>
        {contactMissing && (
          <p className="rounded-md border border-line bg-panel px-3 py-2 text-xs text-status-cancelled">
            Set a MusicBrainz contact email in Workbench → Enrich first.
          </p>
        )}
        {otherBusy && (
          <p className="rounded-md border border-line bg-panel px-3 py-2 text-xs text-muted">
            Another enrich run is in progress — pause or stop it first.
          </p>
        )}
      </section>

      {active && run && live ? (
        <section className="space-y-3 rounded-md border border-line bg-panel px-3 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold tracking-wider text-faint uppercase">
              {run.status === "paused" ? "Paused" : "Enriching…"}
            </p>
            <div className="flex flex-wrap gap-2">
              {run.status === "running" ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={pausePass.isPending}
                  onClick={() => pausePass.mutate()}
                >
                  Pause
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={resumePass.isPending}
                  onClick={() => resumePass.mutate()}
                >
                  Resume
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={stopPass.isPending}
                onClick={() => stopPass.mutate()}
              >
                Stop
              </Button>
            </div>
          </div>

          <div
            className="h-1.5 overflow-hidden rounded-full bg-panel-elevated"
            role="progressbar"
            aria-valuenow={Math.round(live.pct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={cn(
                "h-full rounded-full bg-accent transition-[width] duration-500",
                run.status === "running" && "animate-pulse",
              )}
              style={{ width: `${live.pct}%` }}
            />
          </div>

          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between gap-2">
              <span className="text-muted">Track</span>
              <span className="min-w-0 truncate text-right text-text">
                {run.current_track?.trim() || "—"}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted">Step</span>
              <span className="min-w-0 truncate text-right text-text">
                {run.current_step?.trim() || "—"}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted">Progress</span>
              <span className="tabular-nums text-text">
                {run.tracks_done.toLocaleString()} / {run.tracks_total.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted">Requests</span>
              <span className="tabular-nums text-text">
                {run.requests_made.toLocaleString()} · queue {run.queue_depth}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted">Elapsed</span>
              <span className="tabular-nums text-text">{live.elapsedLabel}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted">ETA</span>
              <span className="tabular-nums text-text">~{live.etaLabel}</span>
            </div>
            {run.current_url && (
              <p className="truncate font-mono text-xs text-faint" title={run.current_url}>
                {run.current_url}
              </p>
            )}
            {run.last_track && (
              <div className="space-y-0.5 border-t border-line pt-1.5">
                <div className="flex justify-between gap-2">
                  <span className="text-muted">Last track</span>
                  <span className="min-w-0 truncate text-right text-text">
                    {run.last_track.name ?? "—"}
                  </span>
                </div>
                <p className="truncate text-right text-xs text-faint">
                  found {formatTrackPulse(run.last_track)}
                </p>
              </div>
            )}
          </div>
        </section>
      ) : (
        <section className="space-y-3">
          <Button
            disabled={
              startBatch.isPending ||
              otherBusy ||
              contactMissing ||
              !estimate ||
              estimate.tracks === 0
            }
            onClick={() =>
              startBatch.mutate({
                size: "all",
                ...(scopedPlaylistId ? { playlist_id: scopedPlaylistId } : {}),
                ...(scopedLocalPlaylistId
                  ? { local_playlist_id: scopedLocalPlaylistId }
                  : {}),
                force: false,
              })
            }
          >
            Fetch data for this {scopeLabel}
          </Button>
          {estimate && (
            <p className="text-xs text-muted">
              ~{estimate.tracks} tracks missing data · est.{" "}
              {formatElapsed(((estimate.time_sec_min + estimate.time_sec_max) / 2) * 1000)} ·{" "}
              {estimate.requests_min}–{estimate.requests_max} requests
            </p>
          )}
        </section>
      )}

      {thisPlaylistRun &&
        run?.summary &&
        run.status !== "running" &&
        run.status !== "paused" && <SummaryBlock summary={run.summary} />}
    </div>
  );
}
